import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/** @type {AbortController | null} */
let checkoutValidationController = null;

/**
 * A custom element that displays a cart note.
 */
class CartNote extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Map<HTMLTextAreaElement, (event: Event) => void>} */
  #boundTextareas = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#bindCharCounters();
    this.#setupCheckoutValidation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindCharCounters();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindCharCounters();
    this.#setupCheckoutValidation();
  }

  /**
   * Handles updates to the cart note or a cart attribute.
   * @param {Event} event - The input event in our text-area.
   */
  updateCartNote = (event) => {
    this.#updateCharCountFromEvent(event);
    this.#saveCartNote(event);
    this.#clearFieldValidity(event);
    syncAcceleratedCheckout();
  };

  #bindCharCounters() {
    const textareas = this.querySelectorAll('textarea[data-char-count]');

    for (const textarea of textareas) {
      if (!(textarea instanceof HTMLTextAreaElement)) continue;
      if (this.#boundTextareas.has(textarea)) {
        this.#setCharCount(textarea, textarea.value.length);
        continue;
      }

      /** @param {Event} event */
      const onInput = (event) => {
        this.#updateCharCountFromEvent(event);
      };

      textarea.addEventListener('input', onInput);
      this.#boundTextareas.set(textarea, onInput);
      this.#setCharCount(textarea, textarea.value.length);
    }
  }

  #unbindCharCounters() {
    for (const [textarea, onInput] of this.#boundTextareas) {
      textarea.removeEventListener('input', onInput);
    }
    this.#boundTextareas.clear();
  }

  /**
   * @param {Event} event
   */
  #updateCharCountFromEvent(event) {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement) || !target.dataset.charCount) return;
    this.#setCharCount(target, target.value.length);
  }

  /**
   * @param {HTMLTextAreaElement} textarea
   * @param {number} length
   */
  #setCharCount(textarea, length) {
    const refName = textarea.dataset.charCount;
    const countEl =
      (refName && this.refs[refName] instanceof Element && this.refs[refName]) ||
      textarea.parentElement?.querySelector('[data-dedication-count], [data-note-count]') ||
      this.querySelector('[data-dedication-count], [data-note-count]');

    if (countEl instanceof Element) {
      countEl.textContent = String(length);
    }
  }

  #setupCheckoutValidation() {
    if (!this.hasAttribute('data-cart-phone-required')) return;

    syncAcceleratedCheckout();

    if (checkoutValidationController) return;

    const controller = new AbortController();
    checkoutValidationController = controller;
    const { signal } = controller;
    const form = document.getElementById('cart-form');

    if (form instanceof HTMLFormElement) {
      form.addEventListener(
        'submit',
        (event) => {
          if (!validateRequiredCartFields()) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        { signal, capture: true }
      );
    }

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const checkoutButton = target.closest('#checkout, .cart__checkout-button, .additional-checkout-buttons');
        if (!checkoutButton) return;

        if (!validateRequiredCartFields()) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { signal, capture: true }
    );
  }

  /**
   * @param {Event} event
   */
  #clearFieldValidity(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-cart-phone]') && target.value.trim()) {
      target.classList.remove('is-invalid');
      setFieldErrorVisible(false);
    }
  }

  /**
   * @param {Event} event
   */
  #saveCartNote = debounce(async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement)) return;

    const attributeName = target.dataset.cartAttribute;
    const value = target instanceof HTMLInputElement ? target.value.trim() : target.value;
    const body = attributeName
      ? { attributes: { [attributeName]: value } }
      : { note: target.value };

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify(body),
      });

      await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('note-update:user-action', event);
    }
  }, 200);
}

/**
 * @returns {HTMLInputElement | null}
 */
function getPhoneInput() {
  const phoneInput = document.querySelector('[data-cart-phone-required] [data-cart-phone]');
  return phoneInput instanceof HTMLInputElement ? phoneInput : null;
}

/**
 * @returns {boolean}
 */
function isPhoneValid() {
  const phoneInput = getPhoneInput();
  return Boolean(phoneInput?.value.trim());
}

/**
 * @param {HTMLElement} element
 */
function revealField(element) {
  const details = element.closest('details');
  if (details instanceof HTMLDetailsElement) {
    details.open = true;
  }
}

/**
 * @param {boolean} visible
 */
function setFieldErrorVisible(visible) {
  const errorEl = document.querySelector('[data-cart-field-error="phone"]');
  if (!(errorEl instanceof HTMLElement)) return;
  errorEl.hidden = !visible;
}

/**
 * @returns {boolean}
 */
function validateRequiredCartFields() {
  const phoneInput = getPhoneInput();

  if (phoneInput && !isPhoneValid()) {
    revealField(phoneInput);
    phoneInput.classList.add('is-invalid');
    setFieldErrorVisible(true);
    phoneInput.focus();
    syncAcceleratedCheckout();
    return false;
  }

  if (phoneInput) {
    phoneInput.classList.remove('is-invalid');
    setFieldErrorVisible(false);
  }

  syncAcceleratedCheckout();
  return true;
}

function syncAcceleratedCheckout() {
  const phoneRequired = document.querySelector('[data-cart-phone-required]');
  const isValid = !phoneRequired || isPhoneValid();

  for (const buttons of document.querySelectorAll('.additional-checkout-buttons')) {
    buttons.classList.toggle('is-phone-blocked', !isValid);
  }
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
